import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MKT } from '../../Marketing/links';
const OVERALL = [
    [
        'S or 6',
        'New or as new: usually a registration-mileage car.'
    ],
    [
        '5',
        'Near new, very low mileage, no visible flaws.'
    ],
    [
        '4.5',
        'Excellent. Low mileage for its age and only the smallest marks.'
    ],
    [
        '4',
        'Good. Some minor scratches or dents a detailer can deal with.'
    ],
    [
        '3.5',
        'Average. Visible marks, often higher mileage; budget for paint or interior work.'
    ],
    [
        '3',
        'Below average. Obvious wear or damage, or very high mileage.'
    ],
    [
        '2',
        'Poor. Significant problems: heavy rust, major dents, mechanical issues.'
    ],
    [
        '1',
        'Often heavily modified, flood or fire damaged, or otherwise far from stock.'
    ],
    [
        'R or RA',
        'Repair history: the car has had structural repair. RA usually marks a lighter repair.'
    ],
    [
        '0 / ***',
        'Not graded: sold as is, often with a note explaining why.'
    ]
];
const INTERIOR = [
    [
        'A',
        'As new.'
    ],
    [
        'B',
        'Very clean, light use.'
    ],
    [
        'C',
        'Normal wear: some marks, stains or cigarette smell.'
    ],
    [
        'D',
        'Dirty or damaged: tears, burns, strong smells.'
    ],
    [
        'E',
        'Very poor: needs serious work.'
    ]
];
export default function AuctionGrades() {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Japanese auction grades are the first thing most buyers look at and the thing they most often misread. A grade is one inspector’s summary of a whole car in a single character. It is very useful for filtering a search, and not enough on its own to bid on. Here is what each Japanese auction grade means, and how to use them together with the rest of the sheet."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The overall grade"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-post__table",
                children: /*#__PURE__*/ _jsxs("table", {
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Grade"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "What it usually means"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: OVERALL.map(([g, m])=>/*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: /*#__PURE__*/ _jsx("strong", {
                                                children: g
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: m
                                        })
                                    ]
                                }, g))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Mileage caps the grade. A clean car with very high kilometres rarely gets above a 3.5, however well it was kept, so a 3.5 can be a better buy than its number suggests: read the damage map and notes to see whether it’s wear or mileage holding it down."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Interior (and exterior) letter grades"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Next to the number sits a letter for the interior, and on many sheets a second one for the exterior."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-post__table",
                children: /*#__PURE__*/ _jsxs("table", {
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Letter"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Condition"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: INTERIOR.map(([g, m])=>/*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: /*#__PURE__*/ _jsx("strong", {
                                                children: g
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: m
                                        })
                                    ]
                                }, g))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "What R and RA really tell you"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "R (or RA) means the car has a repair history, ",
                    /*#__PURE__*/ _jsx("em", {
                        children: "修復歴あり"
                    }),
                    ": a structural part was repaired or replaced. It doesn’t say how big the repair was. A replaced radiator support on an otherwise lovely car is a very different thing from a straightened chassis rail, and the inspector’s notes and damage map usually say which panels were involved."
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "R cars sell for less, which can make them good buys for your own use or for a customer who knows. For retail stock, many shops avoid them or disclose them clearly; either way, tell the buyer."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Grades vary by auction house"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Each auction house trains its own inspectors and the big ones are known to be stricter or more generous. The same car could be a 4 at one venue and a 4.5 at another. Treat half a grade as noise, and compare cars graded at the same house where you can."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Which grades to bid on"
            }),
            /*#__PURE__*/ _jsxs("ul", {
                children: [
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "Retail stock:"
                            }),
                            " 4 and up with a B interior is the safe ground. These cars usually need a detail, not a body shop."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "Value buys:"
                            }),
                            " 3.5 / C with a clean damage map and short notes. Budget for paint correction and interior work and price the recon in before you bid."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "Projects and parts cars:"
                            }),
                            " 3 and below, R/RA, ungraded. Read every note, ask your agent for extra photos, and bid as if the worst line on the sheet is true."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Use the sheet, not just the grade"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "The grade gets a car onto your shortlist; the sheet decides the bid. Read",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: "/blog/how-to-read-a-japanese-auction-sheet",
                        children: "how to read a Japanese auction sheet"
                    }),
                    ' ',
                    "for the rest of it. Right Hand DMS reads the whole sheet into English and prices each lot against comparable sold cars across grades, so you can see what a 3.5 really fetches next to a 4.5: see ",
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.product('pricing-guidance'),
                        children: "pricing guidance"
                    }),
                    "."
                ]
            })
        ]
    });
}
